import React, { useState, useEffect } from 'react';
import { Outlet, Navigate, useLocation, Link } from 'react-router-dom';
import AdminSidebar from '../components/admin/AdminSidebar';
import AdminHeader from '../components/admin/AdminHeader';
import ToastContainer, { showToast } from '../components/common/Toast';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';

import SubscriptionModal from '../components/subscription/SubscriptionModal';
import { AlertCircle, CreditCard, Sparkles, Lock, ShieldAlert, Phone, Copy, Check } from 'lucide-react';

export default function AdminLayout() {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [copiedCard, setCopiedCard] = useState(false);

  const isLawyer = user?.role === 'LAWYER';
  const isClient = user?.role === 'CLIENT' || user?.isClient;
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  // Automatically close sidebar on mobile navigation
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const fetchSubscriptionStatus = () => {
    if (isAuthenticated && (isLawyer || isClient)) {
      api.get('/subscriptions/my-status')
        .then((res) => {
          if (res?.success) {
            setSubStatus(res);
            // Strictly kick out lawyer if access is revoked or expired
            if (isLawyer && !res.isActive && !res.isFreeExempt) {
              localStorage.removeItem('advokat_token');
              localStorage.removeItem('user');
              showToast('Ruxsatingiz ma’muriyat tomonidan bekor qilingan yoki muddati tugagan. Akkauntdan chiqarildingiz.', 'error');
              window.location.href = '/admin/login';
            }
          }
        })
        .catch((err) => {
          if (isLawyer && (err.status === 401 || err.status === 403 || err.message?.includes('bekor qilingan'))) {
            localStorage.removeItem('advokat_token');
            localStorage.removeItem('user');
            showToast('Ruxsatingiz bekor qilingan. Akkauntdan chiqarildingiz.', 'error');
            window.location.href = '/admin/login';
          }
        });
    }
  };

  const fetchPendingCount = () => {
    if (isAuthenticated) {
      api.get('/appointments/stats')
        .then((res) => {
          if (res?.success && res?.stats) {
            setPendingCount(res.stats.pending || 0);
          }
        })
        .catch(() => {});
    }
  };

  useEffect(() => {
    fetchPendingCount();
    fetchSubscriptionStatus();
    const interval = setInterval(() => {
      fetchPendingCount();
      fetchSubscriptionStatus();
    }, 4000); // Fast 4-sec poll to enforce immediate kick-out on admin revocation
    return () => clearInterval(interval);
  }, [isAuthenticated, location.pathname, isLawyer, isClient]);

  const isAccessLocked = (isLawyer || isClient) && subStatus && !subStatus.isActive && !subStatus.isFreeExempt;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#060c17] flex flex-col items-center justify-center text-slate-300 p-4">
        <div className="w-10 h-10 border-2 border-[#c5a880] border-t-transparent rounded-full animate-spin mb-3"></div>
        <div className="font-serif font-bold text-slate-100 text-sm tracking-wider">JIZZAX ADVOKATLARI</div>
        <div className="text-xs text-slate-400 mt-1">Admin tizimi yuklanmoqda...</div>
        <button
          type="button"
          onClick={() => {
            localStorage.removeItem('advokat_token');
            window.location.href = '/admin/login';
          }}
          className="mt-6 text-xs text-[#c5a880] hover:underline cursor-pointer"
        >
          Kirish sahifasiga o‘tish →
        </button>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  const getPageTitle = (path) => {
    if (path.includes('/admin/dashboard')) return { title: 'Dashboard', subtitle: 'Umumiy ko‘rsatkichlar va yangi faolliklar' };
    if (path.includes('/admin/appointments')) return { title: 'Uchrashuvlar Boshqaruvi', subtitle: 'Arizalarni tasdiqlash, rad etish va eksport' };
    if (path.includes('/admin/calendar')) return { title: 'Qabul Taqvim Jadvali', subtitle: 'Kunlik, haftalik va oylik ko‘rinish' };
    if (path.includes('/admin/lawyers')) return { title: 'Advokatlar Ro‘yxati', subtitle: 'Mutaxassislar profillari va ixtisoslashuv' };
    if (path.includes('/admin/services')) return { title: 'Amaliyot Yo‘nalishlari & Xizmatlar', subtitle: 'Yuridik xizmatlar katalogi' };
    if (path.includes('/admin/offices')) return { title: 'Ofislar & Joylashuv', subtitle: 'Filiallar, xarita va koordinatalar' };
    if (path.includes('/admin/working-hours')) return { title: 'Ish Vaqtlari & Ta’tillar', subtitle: 'Qabul jadvallari va dam olish kunlari' };
    if (path.includes('/admin/clients')) return { title: 'Mijozlar Bazasi', subtitle: 'Doimiy va yangi mijozlar tarixi' };
    if (path.includes('/admin/reviews')) return { title: 'Mijozlar Fikrlari & Reyting', subtitle: 'Sharhlarni moderatsiya qilish' };
    if (path.includes('/admin/cms')) return { title: 'Sayt Kontenti (CMS)', subtitle: 'Bosh sahifa, matnlar va bloklarni tahrirlash' };
    if (path.includes('/admin/contact')) return { title: 'Kontakt Rekvizitlari', subtitle: 'Telefonlar, manzillar va aloqa ma’lumotlari' };
    if (path.includes('/admin/social')) return { title: 'Ijtimoiy Tarmoqlar', subtitle: 'Telegram, WhatsApp va LinkedIn havolalari' };
    if (path.includes('/admin/notifications')) return { title: 'Bildirishnomalar Jurnali', subtitle: 'SMS va tizim bildirishnomalari jurnali' };
    if (path.includes('/admin/translations')) return { title: 'Tillar & Tarjimalar Boshqaruvi', subtitle: 'O‘zbek, rus va ingliz tillaridagi lug‘at' };
    if (path.includes('/admin/media')) return { title: 'Media Fayllar Kutubxonasi', subtitle: 'Suratlar va yuklangan hujjatlar' };
    if (path.includes('/admin/settings')) return { title: 'Tizim Sozlamalari', subtitle: 'Vaqt mintaqasi, slot davomiyligi, SMTP va API kalitlari' };
    if (path.includes('/admin/users')) return { title: 'Admin Foydalanuvchilar', subtitle: 'Rollar va xodimlarga ruxsat berish' };
    if (path.includes('/admin/activity-logs')) return { title: 'Audit & Harakatlar Tarixi', subtitle: 'Barcha ma’muriy amallar logi' };
    if (path.includes('/admin/subscriptions')) return { title: 'To‘lovlar & Obunalar Boshqaruvi', subtitle: '30 kunlik muddat, obunachilar va tekin/pullik rejim nazorati' };
    if (path.includes('/admin/chats')) return { title: 'Yozishmalar Nazorati (Audit)', subtitle: 'Advokat va mijozlar shaxsiy chatlari va fayllari monitoringi' };
    if (path.includes('/admin/lawyer-chat')) return { title: 'Mijozlar Bilan Shaxsiy Chat', subtitle: 'Konfidensial yozishma va hujjatlar almashinuvi' };
    return { title: 'Boshqaruv Paneli', subtitle: 'Advokatlar portali boshqaruvi' };
  };

  const { title, subtitle } = getPageTitle(location.pathname);

  return (
    <div className="flex h-screen bg-slate-100 overflow-hidden text-slate-800">
      <AdminSidebar 
        pendingCount={pendingCount} 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        isAccessLocked={isAccessLocked}
      />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <AdminHeader 
          title={title} 
          subtitle={subtitle} 
          pendingCount={pendingCount} 
          onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        />
        <main className="flex-1 overflow-y-auto p-3 sm:p-5 lg:p-6">
          {/* STRICT ACCESS LOCK FOR REVOKED / UNPAID ACCOUNTS */}
          {isAccessLocked && !location.pathname.includes('/admin/subscriptions') ? (
            <div className="max-w-2xl mx-auto my-6 sm:my-10 p-6 sm:p-8 bg-white border-2 border-rose-200/90 rounded-2xl shadow-xl text-center space-y-6 animate-admin-fade-in">
              <div className="w-16 h-16 rounded-2xl bg-rose-50 border border-rose-300 flex items-center justify-center text-rose-600 mx-auto shadow-inner">
                <Lock className="w-8 h-8" />
              </div>

              <div>
                <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-rose-100 text-rose-800 border border-rose-300 inline-block mb-3">
                  Ruxsat To‘xtatilgan / Obuna Muddati Tugagan
                </span>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                  Tizimga Kirish Cheklangan (Xavfsizlik Protokoli)
                </h2>
                <p className="text-xs sm:text-sm text-slate-600 max-w-lg mx-auto mt-2 leading-relaxed">
                  Hurmatli <strong>{user?.fullName}</strong>, ushbu akkaunt uchun xizmatdan foydalanish ruxsati ma’muriyat tomonidan to‘xtatilgan yoki oylik muddat tugagan. Xavfsizlik talablariga muvofiq, tizim imkoniyatlaridan (qabullar, sud kundaligi, chat, dosyelar) foydalanish uchun oylik to‘lovni amalga oshiring yoki ma’muriyat bilan bog‘laning.
                </p>
              </div>

              {/* Official Payment Card Box */}
              <div className="bg-[#091322] border border-[#1b2b46] rounded-xl p-5 text-white text-left max-w-md mx-auto shadow-md">
                <div className="text-[10px] uppercase font-bold tracking-wider text-[#c5a880] mb-1">
                  Rasmiy To‘lov Qabul Qilish Kartasi (Click & Payme)
                </div>
                <div className="font-mono text-xl sm:text-2xl font-extrabold text-white tracking-wider flex items-center justify-between">
                  <span>5614 6814 2744 1112</span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText('5614681427441112');
                      setCopiedCard(true);
                      showToast('Karta raqami nusxalandi: 5614 6814 2744 1112', 'success');
                      setTimeout(() => setCopiedCard(false), 2000);
                    }}
                    className="px-2.5 py-1 rounded text-xs bg-[#17253d] text-[#c5a880] hover:bg-[#223554] border border-[#c5a880]/30 transition-all cursor-pointer font-sans"
                  >
                    {copiedCard ? 'Nusxalandi!' : 'Nusxalash'}
                  </button>
                </div>
                <div className="text-xs text-slate-300 mt-2 flex items-center justify-between">
                  <span>Egasi: <strong className="text-white">JIZZAX ADVOKATLARI</strong></span>
                  <span className="text-[#c5a880] font-bold font-mono">
                    {isLawyer ? '99 000 so‘m/oy' : '35 000 so‘m/oy'}
                  </span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                <Link
                  to="/admin/subscriptions"
                  className="w-full sm:w-auto px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white text-xs font-bold uppercase tracking-wider shadow-md hover:shadow-lg transition-all cursor-pointer flex items-center justify-center gap-2"
                >
                  <CreditCard className="w-4 h-4" />
                  <span>To‘lov Chekini Yuklash & Faollashtirish</span>
                </Link>
                <a
                  href="tel:+998915967755"
                  className="w-full sm:w-auto px-5 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 text-xs font-semibold flex items-center justify-center gap-2 transition-all cursor-pointer"
                >
                  <Phone className="w-3.5 h-3.5" />
                  <span>Admin (+998 91 596-77-55)</span>
                </a>
              </div>
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
      <SubscriptionModal
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        initialType={isLawyer ? 'LAWYER' : 'CLIENT'}
        onSuccess={() => {
          fetchSubscriptionStatus();
        }}
      />
      <ToastContainer />
    </div>
  );
}
